/* eslint-disable no-unused-vars */
/* eslint-disable react/prop-types */
import React, { useRef, useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Globe,
  LogOut,
  User,
  Settings,
  Briefcase,
  Lock,
  CreditCard,
  Layers,
  Grid,
  Shield,
  CheckCircle,
  AlertCircle,
  MapPin,
  ChevronRight,
} from "react-feather";
import { Modal, Tooltip as BootstrapTooltip } from "bootstrap";
import Select from "react-select";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import Swal from "sweetalert2";
import { useNotification } from "../context/NotificationContext";
import {
  Avatar_01,
  Avatar_02,
  lnEnglish,
  lnFrench,
  lnSpanish,
  lnGerman,
} from "../../Routes/ImagePath";
import NotificationPopup from "../pages/hms/components/popups/notificationPopup";
import i18n from "../../i18n";

const normalizeMod = (m) =>
  String(m ?? "").trim().toLowerCase().replace(/_/g, "-");

const safeParseJsonArray = (value) => {
  try {
    const v = JSON.parse(value || "[]");
    return Array.isArray(v) ? v : [];
  } catch {
    return [];
  }
};

const getPortalTarget = () =>
  typeof document !== "undefined" ? document.body : null;

const getAppGroup = (key) => {
  const normalized = normalizeMod(key);

  if (["office", "hms", "pos", "dynamic-price-room"].includes(normalized)) {
    return "Operate";
  }

  if (["channel-manager", "booking-engine", "website", "digital-menu", "table-flow"].includes(normalized)) {
    return "Sell";
  }

  return "Engage";
};

const langOptions = [
  { key: "en", label: "English", flag: lnEnglish },
  { key: "fr", label: "French", flag: lnFrench },
  { key: "es", label: "Spanish", flag: lnSpanish },
  { key: "de", label: "German", flag: lnGerman },
];

const selectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: 42,
    borderRadius: 14,
    borderColor: state.isFocused ? "#2563eb" : "rgba(15, 23, 42, 0.12)",
    boxShadow: state.isFocused ? "0 0 0 3px rgba(37, 99, 235, 0.12)" : "none",
    background: "#ffffff",
    fontSize: 13,
    fontWeight: 800,
    cursor: "pointer",
    "&:hover": {
      borderColor: state.isFocused ? "#2563eb" : "rgba(15, 23, 42, 0.22)",
    },
  }),
  valueContainer: (base) => ({ ...base, padding: "0 12px" }),
  placeholder: (base) => ({ ...base, color: "#94a3b8", fontWeight: 760 }),
  singleValue: (base) => ({ ...base, color: "#0f172a", fontWeight: 900 }),
  menuPortal: (base) => ({ ...base, zIndex: 2147483647 }),
  menu: (base) => ({
    ...base,
    zIndex: 2147483647,
    borderRadius: 14,
    overflow: "hidden",
    border: "1px solid rgba(15, 23, 42, 0.08)",
    boxShadow: "0 18px 42px rgba(15, 23, 42, 0.18)",
  }),
  option: (base, state) => ({
    ...base,
    fontSize: 13,
    fontWeight: state.isSelected ? 900 : 760,
    backgroundColor: state.isSelected ? "#2563eb" : state.isFocused ? "#eff6ff" : "#ffffff",
    color: state.isSelected ? "#ffffff" : "#0f172a",
    cursor: "pointer",
  }),
};

const ProfileMenu = ({
  profilePic,
  gender,
  ProfileName,
  useremail,
  flagImage,
  isLocked,
  handleLockScreenClick,
  handleLogout,
  handleAccountClick,
  handleApiClick,
  handleManageOrgClick,
  changeLanguage,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const submitButtonRef = useRef(null);
  const { t } = useTranslation();

  const cdnUrl = process.env.REACT_APP_CDN_URL;
  const baseUrl = process.env.REACT_APP_BASE_URL;
  const apiUrl = process.env.REACT_APP_API_URL;
  const token = localStorage.getItem("token");

  const [showPopup, setShowPopup] = useState(false);
  const [organizations, setOrganizations] = useState([]);
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [loading, setLoading] = useState(true);
  const { notify } = useNotification();

  const profileName = ProfileName || localStorage.getItem("username") || "User";
  const profileEmail = useremail || localStorage.getItem("useremail") || "";
  const roleLabel = localStorage.getItem("role") || "Team Member";
  const companyName =
    selectedOrg?.label ||
    localStorage.getItem("companyname") ||
    localStorage.getItem("company_name") ||
    localStorage.getItem("company") ||
    "Current Organization";

  const appAccess = useMemo(() => {
    const raw = safeParseJsonArray(localStorage.getItem("app_access"));
    return raw.map(normalizeMod);
  }, []);

  const hasAccessTo = useCallback(
    (key) => appAccess.includes(normalizeMod(key)),
    [appAccess]
  );

  const MODULE_PREFIXES = new Set([
    "hospitality",
    "pos",
    "feedback",
    "digital-menu",
    "booking-engine",
    "channel-manager",
    "table-flow",
    "marketing",
  ]);

  const getModulePrefix = (pathname) => {
    const first = String(pathname || "").split("/")[1] || "";
    return MODULE_PREFIXES.has(first) ? first : "";
  };

  const dualRoute = (modulePrefix, page) => {
    const p = String(page || "").replace(/^\//, "");
    if (!modulePrefix) return `/${p}`;
    return `/${modulePrefix}/${p}`;
  };

  const closeModal = useCallback(() => {
    const modalEl = document.getElementById("profile-menu");
    if (!modalEl) return;

    const modal = Modal.getInstance(modalEl) || new Modal(modalEl);
    modal.hide();
  }, []);

  const closeMenu = useCallback(() => {
    if (submitButtonRef.current) submitButtonRef.current.click();
    closeModal();
  }, [closeModal]);

  useEffect(() => {
    const fetchOrganizations = async () => {
      try {
        setLoading(true);

        const response = await axios.get(`${apiUrl}/company/organizations`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (response.data?.organizations?.length) {
          const mappedOrganizations = response.data.organizations.map((org) => ({
            id: org.company_id,
            name: org.company_name || "Unnamed Organization",
            type: org.type || "N/A",
          }));

          setOrganizations(mappedOrganizations);

          const currentOrg = mappedOrganizations.find((org) => org.type === "Current");
          if (currentOrg) {
            setSelectedOrg({ value: currentOrg.id, label: currentOrg.name });
          }
        } else {
          toast.info("No organizations found.");
        }
      } catch (error) {
        console.error("Error fetching organizations:", error?.message);
        notify("error", "Unable to fetch organizations. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    if (token && apiUrl) fetchOrganizations();
  }, [apiUrl, token, notify]);

  const handleSwitchCompany = useCallback(
    async (orgId, orgName) => {
      Swal.fire({
        title: "Switch organization?",
        text: `Activate ${orgName} for this session?`,
        icon: "question",
        showCancelButton: true,
        confirmButtonColor: "#2563eb",
        cancelButtonColor: "#64748b",
        confirmButtonText: "Activate",
        cancelButtonText: "Cancel",
        customClass: {
          popup: "ns-profile-swal",
        },
      }).then(async (result) => {
        if (!result.isConfirmed) return;

        try {
          const response = await axios.post(
            `${apiUrl}/users/switch-company`,
            { companyid: orgId },
            { headers: { Authorization: `Bearer ${token}` } }
          );

          if (response.status === 200) {
            const data = response.data;

            const sessionItems = {
              token: data.accessToken,
              refreshToken: data.refreshToken,
              companyid: data.companyid,
              company: data.company,
              role: data.role,
              app_access: JSON.stringify(data.app_access || []),
              companies: JSON.stringify(data.companies),
              dateFormat: data.dateformat,
              dateFormatWithTime: data.dateFormatWithTime,
              timezone: data.timezone,
              currencycode: data.currency,
              business_date: data.business_date,
              posbusiness_date: data.posBusinessdate,
            };

            Object.entries(sessionItems).forEach(([key, value]) => {
              localStorage.setItem(key, value);
            });

            notify("success", `Successfully switched to ${orgName}`);
            window.location.reload();
          }
        } catch (error) {
          console.error("Error switching organization:", error);
          notify("error", error.response?.data?.message || "Failed to switch organization");
        }
      });
    },
    [apiUrl, token, notify]
  );

  const handleOrgChange = (selectedOption) => {
    if (selectedOption && selectedOption.value !== selectedOrg?.value) {
      setSelectedOrg(selectedOption);
      handleSwitchCompany(selectedOption.value, selectedOption.label);
    }
  };

  const modulePrefix = useMemo(
    () => getModulePrefix(location.pathname),
    [location.pathname]
  );

  const handleAccountsClick = () => {
    closeMenu();
    setTimeout(() => navigate(dualRoute(modulePrefix, "account-setting")), 180);
  };

  const handleLogouts = () => {
    closeMenu();
    localStorage.clear();
    sessionStorage.clear();
    navigate(baseUrl);
  };

  const handleManageOrgClicks = () => {
    closeMenu();
    setTimeout(() => navigate(dualRoute(modulePrefix, "manage-property")), 180);
  };

  const handleApiClicks = () => {
    closeMenu();
    setTimeout(() => navigate("/api-settings"), 180);
  };

  const guardedNavigate = useCallback(
    (appKey, route) => {
      if (!hasAccessTo(appKey)) {
        setShowPopup(true);
        return;
      }

      closeMenu();
      setTimeout(() => navigate(route), 180);
    },
    [hasAccessTo, navigate, closeMenu]
  );

  const handleAppClick = (appKey) => {
    const key = normalizeMod(appKey);

    switch (key) {
      case "office":
        return guardedNavigate("office", "/employee-dashboard");
      case "hms":
        return guardedNavigate("hms", "/hospitality");
      case "pos":
        return guardedNavigate("pos", "/pos");
      case "feedback":
        return guardedNavigate("feedback", "/feedback");
      case "marketing":
        return guardedNavigate("marketing", "/marketing");
      case "channel-manager":
        return guardedNavigate("channel-manager", "/channel-manager");
      case "table-flow":
        return guardedNavigate("table-flow", "/table-flow");
      case "booking-engine":
        return guardedNavigate("booking-engine", "/booking-engine");
      case "digital-menu":
        return guardedNavigate("digital-menu", "/digital-menu");
      case "dynamic-price-room":
        return guardedNavigate("dynamic-price-room", "/dynamic-price-room");
      case "website":
        return guardedNavigate("website", "/website");
      default:
        return;
    }
  };

  const handleLanguageChange = (lng) => {
    if (isLocked) return;

    if (typeof changeLanguage === "function") {
      changeLanguage(lng);
      return;
    }

    i18n.changeLanguage(lng);
  };

  useEffect(() => {
    const modalEl = document.getElementById("profile-menu");
    if (!modalEl) return;

    let tooltipInstances = [];

    const initTooltips = () => {
      const nodes = modalEl.querySelectorAll('[data-bs-toggle="tooltip"]');
      tooltipInstances = [...nodes].map(
        (el) =>
          BootstrapTooltip.getInstance(el) ||
          new BootstrapTooltip(el, {
            boundary: "window",
            placement: "bottom",
          })
      );
    };

    const destroyTooltips = () => {
      tooltipInstances.forEach((tooltip) => tooltip?.dispose?.());
      tooltipInstances = [];
    };

    modalEl.addEventListener("shown.bs.modal", initTooltips);
    modalEl.addEventListener("hidden.bs.modal", destroyTooltips);

    return () => {
      modalEl.removeEventListener("shown.bs.modal", initTooltips);
      modalEl.removeEventListener("hidden.bs.modal", destroyTooltips);
      destroyTooltips();
    };
  }, []);

  const orgOptions = organizations.map((org) => ({
    value: org.id,
    label: org.name,
  }));

  const apps = [
    { key: "office", icon: "business", label: t("WorkWise"), desc: "Workforce, Payroll & HRMS reimagined." },
    { key: "hms", icon: "hotel", label: t("StayX"), desc: "Smart Hospitality & Property Management." },
    { key: "pos", icon: "point_of_sale", label: t("NovaPOS"), desc: "Next-gen POS for Retail & F&B." },
    { key: "dynamic-price-room", icon: "trending_up", label: t("Dynamic Pricing"), desc: "AI-powered room pricing recommendations & approvals." },
    { key: "channel-manager", icon: "hub", label: t("ChannelSync"), desc: "Sync availability & pricing across OTAs." },
    { key: "booking-engine", icon: "book_online", label: t("BookFlow"), desc: "Seamless Direct Booking Engine." },
    { key: "website", icon: "web", label: t("ShineWeb"), desc: "Modern Business Website Builder." },
    { key: "digital-menu", icon: "restaurant_menu", label: t("MenuNova"), desc: "Dynamic Digital Menu for F&B." },
    { key: "marketing", icon: "campaign", label: t("ShineReach"), desc: "Marketing Hub & CRM." },
    { key: "feedback", icon: "feedback", label: t("Pulse360"), desc: "Collect, analyze & act on feedback." },
    { key: "table-flow", icon: "spoon", label: t("TableFlow"), desc: "Commission-free reservations for restaurants, cafés and bars from your own channels." },
  ];

  const groupedApps = useMemo(() => {
    return apps.reduce((acc, app) => {
      const group = getAppGroup(app.key);
      if (!acc[group]) acc[group] = [];
      acc[group].push(app);
      return acc;
    }, {});
  }, [apps]);

  const profileSrc =
    profilePic && profilePic.trim() !== "" && profilePic !== "null"
      ? `${String(cdnUrl || "").replace(/\/+$/, "")}/${String(profilePic).replace(/^\/+/, "")}`
      : gender === "male"
      ? Avatar_02
      : Avatar_01;

  const currentLanguage = langOptions.find((lang) => lang.key === i18n.language) || langOptions[0];

  return (
    <div id="profile-menu" className="modal custom-modal fade ns-profile-modal" role="dialog" tabIndex="-1">
      <div className="modal-dialog ns-profile-dialog">
        <div className="modal-content ns-profile-content">
          <style>{`
            .ns-profile-modal {
              z-index: 2147483000 !important;
            }

            .ns-profile-modal .modal-backdrop,
            .modal-backdrop.show {
              background: rgba(15, 23, 42, 0.08) !important;
              backdrop-filter: blur(1.5px);
              -webkit-backdrop-filter: blur(1.5px);
            }

            .ns-profile-dialog {
              position: fixed;
              top: 0;
              right: 0;
              bottom: 0;
              margin: 0;
              width: min(520px, 100vw);
              max-width: 100vw;
              height: 100vh;
              height: 100dvh;
              transform: none !important;
            }

            .ns-profile-content {
              height: 100%;
              border: 0;
              border-radius: 0;
              background:
                radial-gradient(circle at top left, rgba(37, 99, 235, 0.13), transparent 34%),
                #f8fafc;
              box-shadow: -18px 0 52px rgba(15, 23, 42, 0.16);
              display: flex;
              flex-direction: column;
              overflow: hidden;
            }

            .ns-profile-header {
              flex: 0 0 auto;
              background:
                radial-gradient(circle at top left, rgba(37, 99, 235, 0.16), transparent 36%),
                linear-gradient(135deg, #ffffff 0%, #f8fbff 100%);
              border-bottom: 1px solid rgba(15, 23, 42, 0.08);
              padding: 18px;
              display: flex;
              align-items: flex-start;
              justify-content: space-between;
              gap: 14px;
            }

            .ns-profile-user {
              display: flex;
              align-items: flex-start;
              gap: 12px;
              min-width: 0;
            }

            .ns-profile-avatar {
              position: relative;
              width: 58px;
              height: 58px;
              border-radius: 20px;
              flex: 0 0 auto;
              box-shadow: 0 16px 32px rgba(37, 99, 235, 0.18);
            }

            .ns-profile-avatar img {
              width: 58px;
              height: 58px;
              border-radius: 20px;
              object-fit: cover;
              border: 2px solid rgba(255, 255, 255, 0.9);
              background: #ffffff;
            }

            .ns-profile-avatar::after {
              content: "";
              position: absolute;
              right: -1px;
              bottom: 3px;
              width: 12px;
              height: 12px;
              border-radius: 50%;
              background: #22c55e;
              border: 2px solid #ffffff;
            }

            .ns-profile-eyebrow {
              color: #2563eb;
              font-size: 0.68rem;
              letter-spacing: 0.14em;
              text-transform: uppercase;
              font-weight: 950;
              margin-bottom: 5px;
            }

            .ns-profile-name {
              color: #001f5b;
              font-size: 1.18rem;
              line-height: 1.14;
              font-weight: 950;
              margin: 0;
              max-width: 320px;
              overflow: hidden;
              text-overflow: ellipsis;
              white-space: nowrap;
            }

            .ns-profile-email {
              color: #64748b;
              font-size: 0.78rem;
              line-height: 1.35;
              font-weight: 760;
              margin-top: 5px;
              max-width: 330px;
              overflow: hidden;
              text-overflow: ellipsis;
              white-space: nowrap;
            }

            .ns-profile-role-row {
              display: flex;
              flex-wrap: wrap;
              align-items: center;
              gap: 7px;
              margin-top: 9px;
            }

            .ns-profile-pill {
              min-height: 26px;
              border-radius: 999px;
              padding: 4px 9px;
              background: #ffffff;
              color: #334155;
              border: 1px solid rgba(15, 23, 42, 0.08);
              display: inline-flex;
              align-items: center;
              gap: 5px;
              font-size: 0.68rem;
              font-weight: 900;
            }

            .ns-profile-pill.success {
              background: #ecfdf5;
              color: #047857;
              border-color: #bbf7d0;
            }

            .ns-profile-close {
              width: 40px;
              height: 40px;
              border-radius: 14px;
              border: 1px solid rgba(220, 38, 38, 0.16);
              background: #fff1f2;
              color: #dc2626;
              display: inline-flex;
              align-items: center;
              justify-content: center;
              cursor: pointer;
              font-size: 1.35rem;
              line-height: 1;
              transition: transform 0.16s ease, background 0.16s ease;
              flex: 0 0 auto;
            }

            .ns-profile-close:hover {
              background: #fee2e2;
              transform: translateY(-1px);
            }

            .ns-profile-body {
              flex: 1 1 auto;
              min-height: 0;
              overflow-y: auto;
              padding: 14px 18px 22px;
              scrollbar-width: thin;
              scrollbar-color: #93c5fd #e2e8f0;
            }

            .ns-profile-card {
              background: #ffffff;
              border: 1px solid rgba(15, 23, 42, 0.08);
              border-radius: 20px;
              box-shadow: 0 10px 26px rgba(15, 23, 42, 0.055);
              padding: 14px;
              margin-bottom: 13px;
            }

            .ns-profile-card-head {
              display: flex;
              align-items: center;
              justify-content: space-between;
              gap: 12px;
              margin-bottom: 11px;
            }

            .ns-profile-card-title {
              color: #0f172a;
              font-size: 0.86rem;
              font-weight: 950;
              margin: 0;
              display: inline-flex;
              align-items: center;
              gap: 8px;
            }

            .ns-profile-card-subtitle {
              color: #64748b;
              font-size: 0.72rem;
              font-weight: 760;
              margin-top: 3px;
            }

            .ns-profile-actions {
              display: grid;
              grid-template-columns: 1fr 1fr;
              gap: 10px;
            }

            .ns-profile-action {
              min-height: 52px;
              border-radius: 17px;
              border: 1px solid rgba(15, 23, 42, 0.08);
              background: #f8fafc;
              color: #0f172a;
              padding: 10px 12px;
              display: flex;
              align-items: center;
              justify-content: space-between;
              gap: 10px;
              font-weight: 950;
              text-align: left;
              cursor: pointer;
              transition: transform 0.16s ease, border-color 0.16s ease, background 0.16s ease;
            }

            .ns-profile-action:hover:not(:disabled) {
              transform: translateY(-1px);
              background: #eff6ff;
              border-color: #bfdbfe;
            }

            .ns-profile-action.danger {
              background: #fff1f2;
              color: #b91c1c;
              border-color: #fecaca;
            }

            .ns-profile-action.danger:hover:not(:disabled) {
              background: #fee2e2;
            }

            .ns-profile-action:disabled,
            .ns-profile-link:disabled,
            .ns-app-card.is-locked {
              opacity: 0.52;
              cursor: not-allowed;
              transform: none !important;
            }

            .ns-profile-action-left {
              display: flex;
              align-items: center;
              gap: 10px;
              min-width: 0;
            }

            .ns-profile-action-icon {
              width: 34px;
              height: 34px;
              border-radius: 13px;
              background: #ffffff;
              color: #1d4ed8;
              border: 1px solid rgba(15, 23, 42, 0.08);
              display: inline-flex;
              align-items: center;
              justify-content: center;
              flex: 0 0 auto;
            }

            .ns-profile-action.danger .ns-profile-action-icon {
              color: #b91c1c;
            }

            .ns-org-loading {
              min-height: 42px;
              display: flex;
              align-items: center;
              color: #64748b;
              font-weight: 820;
              font-size: 0.82rem;
            }

            .ns-language-grid {
              display: grid;
              grid-template-columns: repeat(2, minmax(0, 1fr));
              gap: 8px;
            }

            .ns-lang-btn {
              min-height: 42px;
              border-radius: 15px;
              border: 1px solid rgba(15, 23, 42, 0.08);
              background: #f8fafc;
              color: #334155;
              padding: 8px 10px;
              display: inline-flex;
              align-items: center;
              gap: 8px;
              font-size: 0.78rem;
              font-weight: 900;
              cursor: pointer;
            }

            .ns-lang-btn.active {
              background: #eff6ff;
              color: #1d4ed8;
              border-color: #bfdbfe;
            }

            .ns-lang-btn img {
              width: 20px;
              height: 20px;
              border-radius: 50%;
              object-fit: cover;
            }

            .ns-profile-links {
              display: grid;
              gap: 8px;
            }

            .ns-profile-link {
              width: 100%;
              min-height: 44px;
              border-radius: 15px;
              border: 1px solid rgba(15, 23, 42, 0.08);
              background: #f8fafc;
              color: #334155;
              padding: 9px 11px;
              display: flex;
              align-items: center;
              justify-content: space-between;
              gap: 10px;
              text-align: left;
              font-size: 0.8rem;
              font-weight: 900;
              cursor: pointer;
            }

            .ns-profile-link-left {
              display: flex;
              align-items: center;
              gap: 9px;
              min-width: 0;
            }

            .ns-profile-link-icon {
              width: 32px;
              height: 32px;
              border-radius: 12px;
              background: #ffffff;
              color: #1d4ed8;
              border: 1px solid rgba(15, 23, 42, 0.08);
              display: inline-flex;
              align-items: center;
              justify-content: center;
              flex: 0 0 auto;
            }

            .ns-profile-link.disabled {
              opacity: 0.52;
              cursor: not-allowed;
            }

            .ns-app-group {
              margin-top: 12px;
            }

            .ns-app-group:first-child {
              margin-top: 0;
            }

            .ns-app-group-title {
              color: #64748b;
              font-size: 0.68rem;
              font-weight: 950;
              text-transform: uppercase;
              letter-spacing: 0.10em;
              margin: 0 0 8px;
            }

            .ns-app-grid {
              display: grid;
              grid-template-columns: repeat(2, minmax(0, 1fr));
              gap: 9px;
            }

            .ns-app-card {
              min-height: 90px;
              border-radius: 18px;
              border: 1px solid rgba(15, 23, 42, 0.08);
              background:
                radial-gradient(circle at top right, rgba(37, 99, 235, 0.06), transparent 36%),
                #ffffff;
              padding: 12px;
              cursor: pointer;
              text-align: left;
              display: flex;
              flex-direction: column;
              justify-content: space-between;
              gap: 10px;
              box-shadow: 0 8px 18px rgba(15, 23, 42, 0.045);
              transition: transform 0.16s ease, box-shadow 0.16s ease, border-color 0.16s ease;
              position: relative;
              overflow: hidden;
            }

            .ns-app-card:hover:not(.app-card--disabled):not(.is-locked) {
              transform: translateY(-2px);
              border-color: #bfdbfe;
              box-shadow: 0 16px 36px rgba(15, 23, 42, 0.10);
            }

            .ns-app-card-top {
              display: flex;
              justify-content: space-between;
              align-items: flex-start;
              gap: 8px;
            }

            .ns-app-icon {
              width: 38px;
              height: 38px;
              border-radius: 14px;
              background: #eff6ff;
              color: #1d4ed8;
              display: inline-flex;
              align-items: center;
              justify-content: center;
              font-size: 22px;
              flex: 0 0 auto;
            }

            .ns-app-access {
              width: 22px;
              height: 22px;
              border-radius: 999px;
              background: #ecfdf5;
              color: #047857;
              border: 1px solid #bbf7d0;
              display: inline-flex;
              align-items: center;
              justify-content: center;
              flex: 0 0 auto;
            }

            .app-card--disabled .ns-app-access {
              background: #f1f5f9;
              color: #94a3b8;
              border-color: #e2e8f0;
            }

            .ns-app-card strong {
              color: #0f172a;
              display: block;
              font-size: 0.82rem;
              line-height: 1.18;
              font-weight: 950;
              overflow: hidden;
              text-overflow: ellipsis;
              white-space: nowrap;
            }

            .ns-app-card span span {
              color: #64748b;
              display: block;
              font-size: 0.68rem;
              line-height: 1.32;
              font-weight: 740;
              margin-top: 4px;
              display: -webkit-box;
              -webkit-line-clamp: 2;
              -webkit-box-orient: vertical;
              overflow: hidden;
            }

            .app-card--disabled {
              opacity: 0.58;
              background: #f8fafc;
              box-shadow: none;
              cursor: not-allowed;
            }

            .tooltip-inner {
              max-width: 300px;
              padding: 10px 12px;
              font-size: 12px;
              font-weight: 800;
              background-color: #0f172a;
              color: #fff;
              border-radius: 10px;
              box-shadow: 0 8px 24px rgba(15, 23, 42, 0.22);
              text-align: center;
              zindex: 2000;
            }

            .bs-tooltip-auto[data-popper-placement^=bottom] .tooltip-arrow::before,
            .bs-tooltip-bottom .tooltip-arrow::before {
              border-bottom-color: #0f172a;
            }

            .ns-profile-swal {
              border-radius: 22px !important;
            }

            @media (max-width: 560px) {
              .ns-profile-actions,
              .ns-app-grid,
              .ns-language-grid {
                grid-template-columns: 1fr;
              }

              .ns-profile-dialog {
                width: 100vw;
              }
            }
          `}</style>

          <header className="ns-profile-header">
            <div className="ns-profile-user">
              <span className="ns-profile-avatar">
                <img src={profileSrc} alt={t("User Avatar")} />
              </span>

              <div style={{ minWidth: 0 }}>
                <div className="ns-profile-eyebrow">{t("Profile Menu")}</div>
                <h3 className="ns-profile-name" title={profileName}>{profileName}</h3>
                <div className="ns-profile-email" title={profileEmail}>{profileEmail || "No email available"}</div>

                <div className="ns-profile-role-row">
                  <span className="ns-profile-pill success">
                    <CheckCircle size={13} />
                    {t("Active Session")}
                  </span>
                  <span className="ns-profile-pill" title={roleLabel}>
                    <Shield size={13} />
                    {roleLabel}
                  </span>
                </div>
              </div>
            </div>

            <button type="button" className="ns-profile-close" data-bs-dismiss="modal" aria-label="Close">
              ×
            </button>
          </header>

          <main className="ns-profile-body">
            <section className="ns-profile-card">
              <div className="ns-profile-actions">
                <button
                  type="button"
                  className="ns-profile-action"
                  onClick={() => !isLocked && handleAccountsClick()}
                  disabled={isLocked}
                >
                  <span className="ns-profile-action-left">
                    <span className="ns-profile-action-icon">
                      <User size={17} />
                    </span>
                    <span>{t("My Account")}</span>
                  </span>
                  <ChevronRight size={16} />
                </button>

                <button
                  type="button"
                  className="ns-profile-action danger"
                  onClick={() => !isLocked && handleLogouts()}
                  disabled={isLocked}
                >
                  <span className="ns-profile-action-left">
                    <span className="ns-profile-action-icon">
                      <LogOut size={17} />
                    </span>
                    <span>{t("Sign Out")}</span>
                  </span>
                  <ChevronRight size={16} />
                </button>
              </div>
            </section>

            <section className="ns-profile-card">
              <div className="ns-profile-card-head">
                <div>
                  <h4 className="ns-profile-card-title">
                    <MapPin size={16} />
                    {t("Current Location")}
                  </h4>
                  <div className="ns-profile-card-subtitle" title={companyName}>
                    {companyName}
                  </div>
                </div>
              </div>

              {loading ? (
                <div className="ns-org-loading">{t("Loading organizations...")}</div>
              ) : (
                <Select
                  options={orgOptions}
                  value={selectedOrg}
                  onChange={handleOrgChange}
                  isDisabled={isLocked}
                  placeholder={t("Select Location")}
                  classNamePrefix="ns-profile-select"
                  styles={selectStyles}
                  menuPortalTarget={getPortalTarget()}
                  menuPosition="fixed"
                  menuPlacement="auto"
                  menuShouldScrollIntoView={false}
                />
              )}
            </section>

            <section className="ns-profile-card">
              <div className="ns-profile-card-head">
                <div>
                  <h4 className="ns-profile-card-title">
                    <Globe size={16} />
                    {t("Preferred Language")}
                  </h4>
                  <div className="ns-profile-card-subtitle">
                    {currentLanguage.label}
                  </div>
                </div>
              </div>

              <div className="ns-language-grid">
                {langOptions.map((language) => (
                  <button
                    type="button"
                    key={language.key}
                    className={`ns-lang-btn ${i18n.language === language.key ? "active" : ""}`}
                    onClick={() => handleLanguageChange(language.key)}
                    disabled={isLocked}
                  >
                    <img src={language.flag} alt={language.label} />
                    {t(language.label)}
                  </button>
                ))}
              </div>
            </section>

            <section className="ns-profile-card">
              <div className="ns-profile-card-head">
                <div>
                  <h4 className="ns-profile-card-title">
                    <Settings size={16} />
                    {t("Workspace")}
                  </h4>
                  <div className="ns-profile-card-subtitle">
                    Manage property, security, billing, and session actions.
                  </div>
                </div>
              </div>

              <div className="ns-profile-links">
                <button
                  type="button"
                  className="ns-profile-link"
                  onClick={() => !isLocked && handleManageOrgClicks()}
                  disabled={isLocked}
                >
                  <span className="ns-profile-link-left">
                    <span className="ns-profile-link-icon"><Briefcase size={16} /></span>
                    {t("Manage Property")}
                  </span>
                  <ChevronRight size={15} />
                </button>

                <button
                  type="button"
                  className="ns-profile-link"
                  onClick={() => !isLocked && handleApiClicks()}
                  disabled={isLocked}
                >
                  <span className="ns-profile-link-left">
                    <span className="ns-profile-link-icon"><Settings size={16} /></span>
                    {t("Settings")}
                  </span>
                  <ChevronRight size={15} />
                </button>

                <button type="button" className="ns-profile-link disabled" disabled>
                  <span className="ns-profile-link-left">
                    <span className="ns-profile-link-icon"><CreditCard size={16} /></span>
                    {t("Billing")}
                  </span>
                  <ChevronRight size={15} />
                </button>

                <button type="button" className="ns-profile-link disabled" disabled>
                  <span className="ns-profile-link-left">
                    <span className="ns-profile-link-icon"><Layers size={16} /></span>
                    {t("Change Plan")}
                  </span>
                  <ChevronRight size={15} />
                </button>

                <button
                  type="button"
                  className="ns-profile-link"
                  onClick={(event) => {
                    event.preventDefault();
                    if (!isLocked && typeof handleLockScreenClick === "function") {
                      closeMenu();
                      handleLockScreenClick();
                    }
                  }}
                  disabled={isLocked || typeof handleLockScreenClick !== "function"}
                >
                  <span className="ns-profile-link-left">
                    <span className="ns-profile-link-icon"><Lock size={16} /></span>
                    {t("Lock Screen")}
                  </span>
                  <ChevronRight size={15} />
                </button>
              </div>
            </section>

            <section className="ns-profile-card">
              <div className="ns-profile-card-head">
                <div>
                  <h4 className="ns-profile-card-title">
                    <Grid size={16} />
                    {t("My Apps")}
                  </h4>
                  <div className="ns-profile-card-subtitle">
                    Open available NetShine apps for this user.
                  </div>
                </div>
              </div>

              {["Operate", "Sell", "Engage"].map((group) =>
                groupedApps[group]?.length ? (
                  <div className="ns-app-group" key={group}>
                    <h5 className="ns-app-group-title">{group}</h5>

                    <div className="ns-app-grid">
                      {groupedApps[group].map((app) => {
                        const allowed = hasAccessTo(app.key);
                        const blocked = isLocked || !allowed;

                        return (
                          <button
                            type="button"
                            key={app.key}
                            className={`ns-app-card ${!allowed ? "app-card--disabled" : ""} ${isLocked ? "is-locked" : ""}`}
                            onClick={() => {
                              if (blocked) {
                                if (!isLocked && !allowed) setShowPopup(true);
                                return;
                              }

                              handleAppClick(app.key);
                            }}
                            data-bs-toggle="tooltip"
                            data-bs-placement="bottom"
                            title={app.desc}
                            aria-disabled={blocked}
                          >
                            <span className="ns-app-card-top">
                              <span className="ns-app-icon">
                                <i className="material-icons-outlined">{app.icon}</i>
                              </span>

                              <span className="ns-app-access">
                                {allowed ? <CheckCircle size={13} /> : <AlertCircle size={13} />}
                              </span>
                            </span>

                            <span>
                              <strong>{app.label}</strong>
                              <span>{app.desc}</span>
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ) : null
              )}

              <div style={{ display: "none" }}>
                <button ref={submitButtonRef} data-bs-dismiss="modal" aria-label="Close" type="reset">
                  Submit2
                </button>
              </div>
            </section>
          </main>
        </div>
      </div>

      <NotificationPopup
        show={showPopup}
        onClose={() => setShowPopup(false)}
        message="Access denied to this module. Please contact your administrator."
      />

      <ToastContainer />
    </div>
  );
};

export default ProfileMenu;
